<template>
  <div class="green-screen-bar fixed inset-x-0 top-0 z-[500] flex h-[82px] items-center gap-4 bg-layer-2 px-4 md:px-6 py-[10px]">
    <!-- Green Screen top bar for desktop and tablet: the Regular view's top bar layout, with the
         caption controls as tiles that each open a small menu. -->
    <div class="mr-auto flex">
      <Popover class="relative" v-slot="{ open }">
        <PopoverButton class="top-bar-button hover:bg-button-ghost-mono-active outline-none" data-testid="views-menu-button" :class="{ pressed: open }">
          <dynamic-svg class="bar-icon" src="/svg/stand.svg" />
          <span>{{ $t('session_views') }}</span>
        </PopoverButton>

        <transition v-bind="menuTransition" @before-enter="menuOpened" @after-leave="menuClosed">
          <PopoverPanel class="absolute left-0 z-[1000] mt-1 flex w-screen max-w-max bg-transparent" v-slot="{ close }">
            <div class="w-screen max-w-sm flex-auto rounded-[16px] p-1 text-md bg-layer-3">
              <button class="font-semibold cursor-pointer text-md flex gap-2 p-3 items-center fill-text-neutral-low" v-for="item in views" :key="item.code"
                :class="item.code === 'chromakey' ? 'text-primary' : 'text-text-neutral-high'" :data-testid="`view-option-${item.code}`"
                @click.prevent="() => { emit('view', item.code); close(); }">
                <dynamic-svg :src="item.icon" width="16" height="16" fill="currentColor" fill-opacity="1" />
                {{ $t(item.label) }}
              </button>
              <div class="h-[1px] bg-white/10 my-1 -mx-1"></div>
              <button v-if="overlayCount >= 0" class="font-semibold cursor-pointer text-md flex gap-2 p-3 items-center text-text-neutral-high w-full" data-testid="overlay-link-option"
                @click.prevent="() => { emit('overlay-link'); close(); }">
                <dynamic-svg src="/svg/chromakey.svg" width="16" height="16" fill="currentColor" fill-opacity="1" />
                {{ overlayLabel }}
                <span v-if="overlayCount > 0" class="ml-auto flex items-center gap-1.5 text-[13px] font-medium text-text-neutral-medium" data-testid="overlay-connected-count">
                  <span class="w-2 h-2 rounded-full bg-[#51DBCD]"></span>{{ overlayCount }}
                </span>
              </button>
              <button class="font-semibold cursor-pointer text-md flex gap-2 p-3 items-center text-text-neutral-high" data-testid="keyboard-shortcuts-option"
                @click.prevent="() => { emit('shortcuts'); close(); }">
                <dynamic-svg src="/svg/keyboard.svg" width="16" height="16" fill="currentColor" fill-opacity="1" />
                {{ shortcutsLabel }}
              </button>
            </div>
          </PopoverPanel>
        </transition>
      </Popover>
    </div>

    <div class="flex items-center gap-2">
      <!-- Font size: the same stepper and typed-in value as the Regular view's caption settings -->
      <Popover class="relative" v-slot="{ open }">
        <PopoverButton :class="[tileClass, { [tilePressedClass]: open }]" data-testid="font-size-menu-button">
          <img class="w-6 h-6" src="@/assets/icons/sorenson/font-size.svg" alt="" />
          <span>{{ sizeLabel }}</span>
        </PopoverButton>

        <transition v-bind="menuTransition" @before-enter="menuOpened" @after-leave="menuClosed">
          <PopoverPanel :class="[menuClass, 'left-0']">
            <span :class="menuLabelClass">{{ sizeLabel }}:</span>
            <div class="flex flex-row rounded-[10px] overflow-hidden">
              <button :disabled="fontSize <= minFontSize" class="bg-button-secondary-mono-resting hover:bg-button-secondary-mono-active h-9 w-10 px-2 py-1 flex justify-center items-center cursor-pointer disabled:opacity-50 disabled:cursor-default"
                data-testid="font-decrease" aria-label="Decrease Font Size" @click="emit('step-font-size', -1)">
                <img width="24" height="24" src="@/assets/icons/minus.svg" alt="-" />
              </button>
              <button :disabled="fontSize >= maxFontSize" class="bg-button-secondary-mono-resting hover:bg-button-secondary-mono-active h-9 w-10 px-2 py-1 flex justify-center items-center border-l-[3px] border-layer-3 cursor-pointer disabled:opacity-50 disabled:cursor-default"
                data-testid="font-increase" aria-label="Increase Font Size" @click="emit('step-font-size', 1)">
                <img width="24" height="24" src="@/assets/icons/plus.svg" alt="+" />
              </button>
            </div>
            <input
                v-model="fontSizeDraft"
                type="number"
                inputmode="numeric"
                :min="minFontSize"
                :max="maxFontSize"
                step="1"
                :aria-label="sizeLabel"
                class="font-size-input border-2 border-[#292F36] rounded-[10px] bg-transparent text-text-neutral-extreme font-normal text-[16px] leading-[1.18rem] h-9 w-[64px] px-[10px] outline-none focus:border-[#6CBFFF]"
                data-testid="font-size-input"
                @change="commitFontSize"
                @keydown.enter="$event.target.blur()"
            />
          </PopoverPanel>
        </transition>
      </Popover>

      <!-- Line count: each click steps to the next count, wrapping 3 back to 1 -->
      <button :class="tileClass" data-testid="line-count-toggle" :data-line-count="lines"
        :aria-label="`${lineCountLabel}: ${lines}`" @click="emit('lines', nextLineCount(lines))">
        <span class="h-6 text-[20px] leading-6 font-bold">{{ lines }}</span>
        <span>{{ lineCountLabel }}</span>
      </button>

      <!-- Width, alignment and font: pick one of a few options -->
      <Popover class="relative" v-slot="{ open }" v-for="menu in choiceMenus" :key="menu.key">
        <PopoverButton :class="[tileClass, { [tilePressedClass]: open }]" :data-testid="`${menu.key}-menu-button`">
          <img class="w-6 h-6" :src="menu.icon" alt="" />
          <span>{{ menu.label }}</span>
        </PopoverButton>

        <transition v-bind="menuTransition" @before-enter="menuOpened" @after-leave="menuClosed">
          <PopoverPanel :class="[menuClass, menu.alignRight ? 'right-0' : 'left-0']" role="radiogroup" :aria-label="menu.label">
            <span :class="menuLabelClass">{{ menu.label }}:</span>
            <button
                v-for="option in menu.options"
                :key="option.value"
                role="radio"
                :aria-checked="menu.value === option.value"
                class="flex items-center gap-1.5 h-9 px-3 rounded-[10px] text-[14px] leading-[20px] font-medium text-text-neutral-high cursor-pointer hover:bg-button-secondary-mono-active"
                :class="menu.value === option.value ? 'bg-button-secondary-mono-active' : 'bg-button-secondary-mono-resting'"
                :style="option.css ? { fontFamily: option.css } : null"
                :data-testid="`${menu.key}-option-${option.value}`"
                @click="emit(menu.key, option.value)"
            >
              <svg v-if="menu.value === option.value" class="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path fill-rule="evenodd" clip-rule="evenodd" :d="CHECK_ICON_PATH" fill="currentColor" />
              </svg>
              <img v-if="menu.optionIcons" class="w-5 h-5 shrink-0" :src="option.icon" alt="" />
              {{ option.label }}
            </button>
          </PopoverPanel>
        </transition>
      </Popover>

      <!-- Background: the chroma key color, filled in by the room -->
      <Popover class="relative" v-slot="{ open }">
        <PopoverButton :class="[tileClass, { [tilePressedClass]: open }]" data-testid="background-color-picker">
          <img class="w-6 h-6" src="@/assets/icons/palette.svg" alt="" />
          <span>{{ backgroundLabel }}</span>
        </PopoverButton>

        <transition v-bind="menuTransition" @before-enter="menuOpened" @after-leave="menuClosed">
          <PopoverPanel :class="[menuClass, 'right-0 !items-start !p-4']">
            <span :class="[menuLabelClass, 'leading-[40px]']">{{ backgroundLabel }}:</span>
            <slot name="background"></slot>
          </PopoverPanel>
        </transition>
      </Popover>

      <TooltipWrap hover :content="$t('leave_or_end_session_tooltip')" :arrow="false">
        <button class="min-w-[64px] top-bar-button top-bar-button--leave bg-button-secondary-destructive-resting hover:bg-button-secondary-destructive-active" @click="emit('leave')">
          <img src="@/assets/icons/sorenson/align-right-01.svg" alt="Leave">
          <span class="text-text-neutral-red">{{ $t('session_leave') }}</span>
        </button>
      </TooltipWrap>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import DynamicSvg from '@/components/ui/DynamicSvg.vue';
import TooltipWrap from '@/components/ui/TooltipWrap.vue';
import { translate, useFontSizeDraft } from '@/composables/useCaptionSettings';
import {
  CHECK_ICON_PATH,
  GREEN_SCREEN_BAR_EMITS,
  GREEN_SCREEN_BAR_PROPS,
  GREEN_SCREEN_VIEWS as views,
  nextLineCount,
  useGreenScreenBar,
} from '@/composables/useGreenScreenBar';

const props = defineProps(GREEN_SCREEN_BAR_PROPS)

const emit = defineEmits(GREEN_SCREEN_BAR_EMITS)

const tileClass = 'flex flex-col items-center justify-center gap-1 min-w-[76px] lg:min-w-[96px] h-[58px] px-3 rounded-[12px] text-xs font-medium whitespace-nowrap text-text-neutral-high bg-button-secondary-mono-resting hover:bg-button-secondary-mono-active cursor-pointer outline-none'
const tilePressedClass = '!bg-button-secondary-mono-active'
const menuClass = 'absolute z-[1000] mt-2 flex w-max items-center gap-2 whitespace-nowrap rounded-[16px] bg-layer-3 p-2'
const menuLabelClass = 'px-2 text-[12px] leading-[16px] font-medium text-text-neutral-high'

const menuTransition = {
  enterActiveClass: 'transition ease-out duration-200',
  enterFromClass: 'opacity-0 translate-y-1',
  enterToClass: 'translate-y-0',
  leaveActiveClass: 'transition ease-in duration-150',
  leaveFromClass: 'translate-y-0',
  leaveToClass: 'opacity-0 translate-y-1',
}

// Opening one menu closes the previous one, and the new menu can start entering before the
// old one has finished leaving, so count them rather than flipping a flag.
const openMenus = ref(0)
const menuOpened = () => {
  openMenus.value += 1
  emit('menu-state', true)
}
const menuClosed = () => {
  openMenus.value = Math.max(0, openMenus.value - 1)
  if (openMenus.value === 0) {
    emit('menu-state', false)
  }
}

const { sizeLabel, lineCountLabel, backgroundLabel, choiceMenus } = useGreenScreenBar(props)

const shortcutsLabel = computed(() => translate('shortcuts_title', 'Keyboard shortcuts'))
const overlayLabel = computed(() => translate('green_screen_overlay_link', 'OBS overlay'))

const { fontSizeDraft, commitFontSize } = useFontSizeDraft(props, emit)
</script>

<style scoped>
/* The app-wide tooltip background is a 5% white wash that reads as dark only over the
   Regular view's dark page; over the chroma color it lets the green through. This is the
   same wash flattened over --color-layer-1, so the Leave tooltip looks as it does there. */
.green-screen-bar {
  --popper-theme-background-color: #171C22;
  --popper-theme-background-color-hover: #171C22;
}

.font-size-input {
  -moz-appearance: textfield;
  appearance: textfield;
  text-align: center;
}

.font-size-input::-webkit-outer-spin-button,
.font-size-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
